import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, FileText, Mail, AlertCircle } from 'lucide-react';

const WarrantyInfo = () => {
  const covered = [
    'Manufacturing defects in materials',
    'Defects in workmanship',
    'Structural failures under normal use',
    'Hardware and mechanism malfunctions',
    'Fabric or upholstery defects',
  ];

  const notCovered = [
    'Normal wear and tear',
    'Accidental damage or misuse',
    'Improper assembly or installation',
    'Stains, burns, or scratches',
    'Modifications or alterations',
    'Commercial or rental use',
  ];

  const claimSteps = [
    {
      step: 1,
      title: 'Document the Issue',
      description: 'Take clear photos showing the defect from multiple angles',
    },
    {
      step: 2,
      title: 'Contact Us',
      description: 'Email warranty@skyfurniture.ca with photos and order details',
    },
    {
      step: 3,
      title: 'Review Process',
      description: 'We review your claim within 2-3 business days',
    },
    {
      step: 4,
      title: 'Resolution',
      description: 'Repair, replacement, or refund based on assessment',
    },
  ];

  return (
    <div className="min-h-screen bg-dark-50">
      
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-dark-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm font-body">
            <Link to="/" className="text-primary-600 hover:text-primary-700">Home</Link>
            <span className="text-dark-400">/</span>
            <span className="text-dark-700 font-semibold">Warranty Information</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-primary py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-fade-in">
            <Shield className="w-16 h-16 mx-auto mb-6 opacity-90" />
            <h1 className="text-4xl text-dark-900 md:text-5xl lg:text-6xl font-display font-bold mb-6">
              Warranty Information
            </h1>
            <p className="text-xl md:text-2xl text-dark-700 max-w-3xl mx-auto font-body">
              Quality furniture backed by our comprehensive warranty protection
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        
        {/* Warranty Overview */}
        <div className="bg-white rounded-2xl shadow-soft p-8 md:p-12 mb-12 animate-slide-up">
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-20 h-20 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-10 h-10 text-accent-600" />
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-dark-900 mb-4">
              1-Year Manufacturer Warranty
            </h2>
            <p className="text-lg text-dark-600 font-body mb-6">
              All Sky Furniture products come with a comprehensive 1-year warranty covering manufacturing defects and workmanship issues from the date of delivery.
            </p>
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-success/10 border border-success rounded-xl text-success font-body font-semibold">
              <CheckCircle className="w-5 h-5" />
              Free Repair or Replacement
            </div>
          </div>
        </div>

        {/* What's Covered */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          
          <div className="bg-white rounded-2xl shadow-soft p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-success" />
              </div>
              <h3 className="text-2xl font-display font-bold text-dark-900">
                What's Covered
              </h3>
            </div>
            
            <ul className="space-y-4">
              {covered.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                  <span className="text-dark-700 font-body">{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 p-4 bg-success/5 rounded-lg">
              <p className="text-sm text-dark-600 font-body">
                <strong>Coverage includes:</strong> Free repair or replacement of defective items, including all labor and shipping costs.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-soft p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-error/10 rounded-lg flex items-center justify-center">
                <XCircle className="w-6 h-6 text-error" />
              </div>
              <h3 className="text-2xl font-display font-bold text-dark-900">
                What's Not Covered
              </h3>
            </div>
            
            <ul className="space-y-4">
              {notCovered.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
                  <span className="text-dark-700 font-body">{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 p-4 bg-error/5 rounded-lg">
              <p className="text-sm text-dark-600 font-body">
                <strong>Note:</strong> Warranty is void if furniture is used for commercial purposes or modified in any way.
              </p>
            </div>
          </div>
        </div>

        {/* Claim Process */}
        <div className="mb-12">
          <h2 className="text-3xl font-display font-bold text-dark-900 mb-8 text-center">
            How to File a Warranty Claim
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {claimSteps.map((item, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-card p-6 text-center hover:shadow-soft transition-smooth"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-primary-600" />
                </div>
                <div className="w-10 h-10 bg-gradient-primary text-white rounded-full flex items-center justify-center mx-auto mb-3 font-display font-bold text-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-display font-bold text-dark-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-dark-600 font-body text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Important Information */}
        <div className="bg-warning/10 border-2 border-warning rounded-2xl p-8 mb-12">
          <div className="flex items-start gap-4 mb-6">
            <AlertCircle className="w-8 h-8 text-warning flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-2xl font-display font-bold text-dark-900 mb-4">
                Important Warranty Information
              </h2>
            </div>
          </div>
          
          <div className="space-y-4 ml-12">
            <div className="p-4 bg-white rounded-lg">
              <h3 className="font-display font-bold text-dark-900 mb-2">📋 Keep Your Receipt</h3>
              <p className="text-dark-700 font-body text-sm">
                Proof of purchase is required for all warranty claims. Keep your order confirmation email and delivery receipt.
              </p>
            </div>

            <div className="p-4 bg-white rounded-lg">
              <h3 className="font-display font-bold text-dark-900 mb-2">📸 Document Upon Delivery</h3>
              <p className="text-dark-700 font-body text-sm">
                Inspect furniture immediately upon delivery. Report any damage within 48 hours for fastest resolution.
              </p>
            </div>

            <div className="p-4 bg-white rounded-lg">
              <h3 className="font-display font-bold text-dark-900 mb-2">🔧 Professional Repairs Only</h3>
              <p className="text-dark-700 font-body text-sm">
                Warranty is void if repairs are attempted by unauthorized persons. Always contact us first.
              </p>
            </div>

            <div className="p-4 bg-white rounded-lg">
              <h3 className="font-display font-bold text-dark-900 mb-2">⏱️ Response Time</h3>
              <p className="text-dark-700 font-body text-sm">
                All warranty claims are reviewed within 2-3 business days. Resolution typically takes 7-10 business days.
              </p>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white rounded-2xl shadow-soft p-8 md:p-12 mb-12">
          <h2 className="text-3xl font-display font-bold text-dark-900 mb-6 text-center">
            Warranty Support
          </h2>
          
          <div className="max-w-2xl mx-auto">
            <p className="text-lg text-dark-600 font-body text-center mb-8">
              Our warranty team is here to help with any questions or claims
            </p>

            <div className="p-6 bg-primary-50 border border-primary-200 rounded-xl text-center">
              <Mail className="w-10 h-10 text-primary-600 mx-auto mb-3" />
              <h3 className="font-display font-bold text-dark-900 mb-2">Email for Claims</h3>
              <a href="mailto:skyfurniturecanada@gmail.com" className="text-primary-600 hover:underline font-body text-lg">
                skyfurniturecanada@gmail.com
              </a>
              <p className="text-sm text-dark-600 font-body mt-3">
                Include: Order number, photos, and description of issue
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-primary border border-primary-300 text-primary-700 rounded-xl font-body font-semibold text-lg hover:shadow-colored transition-smooth"
          >
            <Mail className="w-6 h-6" />
            File a Warranty Claim
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WarrantyInfo;